import { USER_TYPE } from '../domains/people-access/constants/copy.js';
import { GROUP_PERMISSIONS } from '../domains/people-access/constants/groupPermissions.js';
import { PERMISSIONS } from '../domains/people-access/constants/permissions.js';
import { envelope } from '../shared/utils/mockServiceHelpers.js';

/**
 * MOCK permissions service (CT-5569). contract TBD (ARP/backend, ARP-1480): the endpoint,
 * the shape and the key catalog are guesses. The actor is the signed-in user with their
 * `user_type` and the permission keys they hold (role grants + direct grants, merged).
 */

/** Mock actors for manual QA (admin with everything by default, a limited admin, none, members only, groups view/manage, full) and tests. */
export const MOCK_ACTORS = Object.freeze({
  // The default signed-in user: an Admin who also holds every permission key, including the one to
  // remove other Admins, so every action of the screens can be tried without switching.
  admin: Object.freeze({
    user_id: 'u-admin',
    user_type: USER_TYPE.ADMIN,
    permissions: Object.values(PERMISSIONS),
  }),
  // An Admin with no explicit permission: sees everything by type, but cannot remove another Admin.
  adminLimited: Object.freeze({ user_id: 'u-admin-limited', user_type: USER_TYPE.ADMIN, permissions: [] }),
  none: Object.freeze({ user_id: 'u-none', user_type: USER_TYPE.MEMBER, permissions: [] }),
  membersOnly: Object.freeze({
    user_id: 'u-members',
    user_type: USER_TYPE.MEMBER,
    permissions: [PERMISSIONS.MEMBERS_VIEW],
  }),
  full: Object.freeze({
    user_id: 'u-full',
    user_type: USER_TYPE.MEMBER,
    permissions: Object.values(PERMISSIONS),
  }),
  groupsViewOnly: Object.freeze({
    user_id: 'u-groups-view',
    user_type: USER_TYPE.MEMBER,
    permissions: [GROUP_PERMISSIONS.VIEW],
  }),
  groupsManager: Object.freeze({
    user_id: 'u-groups-manager',
    user_type: USER_TYPE.MEMBER,
    permissions: Object.values(GROUP_PERMISSIONS),
  }),
});

let mockActor = MOCK_ACTORS.admin;

/**
 * Manual QA switch (development builds only): open any People & Access URL with
 * `?mockActor=admin|adminLimited|none|membersOnly|groupsViewOnly|groupsManager|full` and reload to act as that viewer; the choice is
 * kept for the browser session. `?mockActor=reset` goes back to the default (admin).
 *
 * The param is captured when this module loads (core-shell imports the app at boot): the
 * shell's workspace redirect strips the query string before the permissions are requested.
 */
const MOCK_ACTOR_PARAM = 'mockActor';
const MOCK_ACTOR_STORAGE_KEY = 'people-access:mock-actor';

const isMockActorKey = (key) => typeof key === 'string' && Object.hasOwn(MOCK_ACTORS, key);

const readQueryParam = () => {
  try {
    return new URLSearchParams(window.location.search).get(MOCK_ACTOR_PARAM);
  } catch {
    return null;
  }
};

const readStoredKey = () => {
  try {
    return window.sessionStorage.getItem(MOCK_ACTOR_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredKey = (key) => {
  try {
    if (key) window.sessionStorage.setItem(MOCK_ACTOR_STORAGE_KEY, key);
    else window.sessionStorage.removeItem(MOCK_ACTOR_STORAGE_KEY);
  } catch {
    // Storage can be blocked (private windows); the URL param still works for this load.
  }
};

/** Stores (or clears) the actor named in the URL and returns the param it saw. */
const captureDevActorFromUrl = () => {
  if (!import.meta.env.DEV) return null;
  const fromUrl = readQueryParam();
  if (fromUrl === 'reset') writeStoredKey(null);
  else if (isMockActorKey(fromUrl)) writeStoredKey(fromUrl);
  return fromUrl;
};

captureDevActorFromUrl();

const resolveDevActor = () => {
  if (!import.meta.env.DEV) return null;

  const fromUrl = captureDevActorFromUrl();
  const key = fromUrl === 'reset' ? null : isMockActorKey(fromUrl) ? fromUrl : readStoredKey();
  return isMockActorKey(key) ? MOCK_ACTORS[key] : null;
};

export const __setMockActorForTests = (actor) => {
  mockActor = actor;
};

export const __resetMockPermissionsForTests = () => {
  mockActor = MOCK_ACTORS.admin;
  writeStoredKey(null);
};

// TODO(CT-5569 / backend): GET /people-access/me/permissions
export const getActorPermissions = async () =>
  envelope({ actor: { ...(resolveDevActor() ?? mockActor) } });
